import type { FetchEmlItem } from "@/lib/api/storage";
import {
  enqueueInboxArchive,
  fetchInboxArchiveUids,
  fetchMailEmlBatch,
  fetchMailStorageStats,
  fetchMailStoredUids,
  warmMailAttachmentCacheBatch,
} from "@/lib/api/storage";
import {
  MAIL_STORAGE_ATTACHMENT_WARM_BATCH,
  MAIL_STORAGE_FETCH_BATCH,
  MAIL_STORAGE_FETCH_GAP_MS,
  MAIL_STORAGE_INBOX_MAX,
  MAIL_STORAGE_WORKER_MAX_STALE_KICKS,
  MAIL_STORAGE_WORKER_POLL_MS,
  MAIL_STORAGE_WORKER_STALE_MS,
} from "@/lib/constants/mailStorage";
import { navFolderSlot } from "@/lib/mail/folderNavSections";
import {
  markInboxArchiveInflight,
  resetInboxArchiveSession,
} from "@/lib/storage/inboxArchiveSession";
import { setFolderStorageProgress } from "@/lib/storage/folderStorageProgress";
import {
  inboxArchiveCoveragePercent,
} from "@/lib/storage/inboxArchiveStatus";
import { storeFetchedEmlItems } from "@/lib/storage/storeFetchedEmlItems";
import { notifyMailStorageStatsRefresh } from "@/features/accounts/MailStorageQuotaSection";

type FolderKey = string;

const syncInflight = new Map<FolderKey, Promise<number>>();

function folderKey(accountId: number, folder: string): FolderKey {
  return `${accountId}\0${folder}`;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function normalizeUids(uids: number[], max = MAIL_STORAGE_INBOX_MAX): number[] {
  return [...new Set(uids.filter((uid) => uid > 0))].slice(0, max);
}

async function resolveArchiveUids(
  accountId: number,
  folder: string,
  fallbackUids: number[]
): Promise<{ target: number; missing: number[]; archiveUids: number[] }> {
  const isInbox = navFolderSlot(folder, "") === "inbox";
  if (isInbox) {
    const plan = await fetchInboxArchiveUids(accountId);
    if (plan.ok && plan.data.success && plan.data.missing.length >= 0) {
      return {
        target: plan.data.target || MAIL_STORAGE_INBOX_MAX,
        missing: plan.data.missing,
        archiveUids: plan.data.uids,
      };
    }
  }

  const storedRes = await fetchMailStoredUids(accountId, folder);
  const storedSet = new Set(
    storedRes.ok && storedRes.data.success ? storedRes.data.uids : []
  );
  const targetUids = normalizeUids(fallbackUids, MAIL_STORAGE_INBOX_MAX);
  return {
    target: MAIL_STORAGE_INBOX_MAX,
    missing: targetUids.filter((uid) => !storedSet.has(uid)),
    archiveUids: targetUids,
  };
}

/** Rattrapage cache PJ preview depuis les .eml déjà archivés (sans ouverture manuelle). */
async function warmStoredAttachmentCache(
  accountId: number,
  folder: string,
  uids: number[]
): Promise<void> {
  const unique = [...new Set(uids.filter((uid) => uid > 0))];
  if (unique.length === 0) return;

  for (let i = 0; i < unique.length; i += MAIL_STORAGE_ATTACHMENT_WARM_BATCH) {
    const batch = unique.slice(i, i + MAIL_STORAGE_ATTACHMENT_WARM_BATCH);
    await warmMailAttachmentCacheBatch({ accountId, folder, uids: batch });
    if (i + MAIL_STORAGE_ATTACHMENT_WARM_BATCH < unique.length) {
      await delay(MAIL_STORAGE_FETCH_GAP_MS);
    }
  }
}

function countBatchSuccess(items: FetchEmlItem[]): number {
  return items.filter((item) => !item.error && (item.stored === true || item.skipped === true)).length;
}

/** Fetch .eml côté client (fallback si worker inactif ou derniers UID récalcitrants). */
async function syncInboxViaClientBatches(
  accountId: number,
  folder: string,
  missing: number[],
  target: number,
  archiveUids: number[],
  cancelled: () => boolean
): Promise<number> {
  if (missing.length === 0) return 0;

  setFolderStorageProgress(accountId, folder, {
    active: true,
    progress: inboxArchiveCoveragePercent(target, missing.length),
    label: `Copie des e-mails… (rattrapage ${missing.length} restant${missing.length > 1 ? "s" : ""})`,
    error: null,
    done: target - missing.length,
    total: target,
  });

  let totalStored = 0;
  let done = 0;
  let lastError: string | null = null;

  for (let i = 0; i < missing.length; i += MAIL_STORAGE_FETCH_BATCH) {
    if (cancelled()) break;
    const batch = missing.slice(i, i + MAIL_STORAGE_FETCH_BATCH);
    const label = `Copie des e-mails… ${done}/${missing.length} (objectif ${target})`;

    const res = await fetchMailEmlBatch({
      accountId,
      folder,
      uids: batch,
      // Rattrapage massif : pas de warm PJ (évite OOM / 500). Warm plus tard si coverage OK.
      warmAttachments: false,
      syncProgress: {
        active: true,
        done,
        total: missing.length,
        label,
      },
    });

    if (!res.ok) {
      lastError = res.message;
      setFolderStorageProgress(accountId, folder, {
        active: false,
        error: res.message,
        label: "",
      });
      break;
    }

    const batchStored = await storeFetchedEmlItems(accountId, folder, res.data.items);
    const batchOk = countBatchSuccess(res.data.items);
    if (batchOk === 0 && batch.length > 0) {
      const fail = res.data.items.find((item) => item.storeError || item.error);
      lastError =
        fail?.error ||
        (fail?.storeError === "write_failed"
          ? "Écriture impossible sur le serveur — vérifiez les droits ou contactez le support."
          : "Copie sur le serveur échouée.");
    }

    totalStored += batchStored;
    done += batchOk;
    setFolderStorageProgress(accountId, folder, {
      progress: Math.round((done / missing.length) * 100),
      done: target - missing.length + done,
      total: target,
      label,
      error: lastError,
    });
    notifyMailStorageStatsRefresh();

    if (i + MAIL_STORAGE_FETCH_BATCH < missing.length) {
      await delay(MAIL_STORAGE_FETCH_GAP_MS);
    }
  }

  await fetchMailEmlBatch({
    accountId,
    folder,
    uids: [],
    syncProgress: {
      active: false,
      done: missing.length,
      total: missing.length,
      label: cancelled() ? "Copie interrompue" : "Copie terminée",
    },
  }).catch(() => undefined);

  const statsAfter = await fetchMailStorageStats(accountId);
  const missingAfter =
    statsAfter.ok && statsAfter.data.success ? statsAfter.data.inboxMissing ?? 0 : 0;
  const targetAfter = statsAfter.ok ? statsAfter.data.inboxTarget ?? target : target;

  setFolderStorageProgress(accountId, folder, {
    active: false,
    progress: missingAfter <= 0 ? 100 : inboxArchiveCoveragePercent(targetAfter, missingAfter),
    label: lastError
      ? lastError
      : missingAfter <= 0
        ? `Réception à jour (${targetAfter}/${targetAfter})`
        : totalStored > 0
          ? `${totalStored} e-mail${totalStored > 1 ? "s" : ""} copié${totalStored > 1 ? "s" : ""} · ${missingAfter} restant${missingAfter > 1 ? "s" : ""}`
          : cancelled()
            ? "Copie interrompue"
            : `Réception : ${missingAfter} e-mail${missingAfter > 1 ? "s" : ""} en attente`,
    error: lastError,
    done: targetAfter - missingAfter,
    total: targetAfter,
  });
  notifyMailStorageStatsRefresh();

  if (missingAfter <= 0) {
    void warmStoredAttachmentCache(accountId, folder, archiveUids);
  }

  return totalStored;
}

/** Archivage INBOX via worker serveur (queue DD02) — le client poll la progression. */
async function syncInboxViaWorker(
  accountId: number,
  folder: string,
  target: number,
  archiveUids: number[],
  cancelled: () => boolean
): Promise<number> {
  let lastStored = -1;
  let lastMissing = -1;
  let lastKick = Date.now();
  let initialStored = 0;
  let staleKicks = 0;

  const kick = async () => {
    await enqueueInboxArchive(accountId);
    lastKick = Date.now();
  };

  await kick();

  const stats0 = await fetchMailStorageStats(accountId);
  if (stats0.ok) {
    initialStored = stats0.data.inboxStored ?? 0;
    lastStored = initialStored;
  }

  setFolderStorageProgress(accountId, folder, {
    active: true,
    progress: 0,
    label: `Copie des e-mails… ${initialStored}/${target}`,
    error: null,
    done: initialStored,
    total: target,
  });

  while (!cancelled()) {
    const stats = await fetchMailStorageStats(accountId);
    if (!stats.ok) break;

    const stored = stats.data.inboxStored ?? 0;
    const missing = stats.data.inboxMissing ?? 0;
    const inboxTarget = stats.data.inboxTarget ?? target;
    const syncLabel = stats.data.sync?.label;
    const coverage = inboxArchiveCoveragePercent(inboxTarget, missing);
    const label =
      syncLabel ||
      `Copie des e-mails… ${inboxTarget - missing}/${inboxTarget}${missing > 0 ? ` · ${missing} restant${missing > 1 ? "s" : ""}` : ""}`;
    const backgroundOnly = missing > 0 && missing <= 2 && coverage >= 98;

    setFolderStorageProgress(accountId, folder, {
      active: missing > 0 && !backgroundOnly,
      progress: coverage,
      done: inboxTarget - missing,
      total: inboxTarget,
      label: backgroundOnly ? `${label} (arrière-plan)` : label,
      error: null,
    });
    notifyMailStorageStatsRefresh();

    if (missing <= 0 || stats.data.inboxArchiveUpToDate) {
      setFolderStorageProgress(accountId, folder, {
        active: false,
        progress: 100,
        label: `Réception à jour (${inboxTarget}/${inboxTarget})`,
        done: inboxTarget,
        total: inboxTarget,
      });
      notifyMailStorageStatsRefresh();
      void warmStoredAttachmentCache(accountId, folder, archiveUids);
      return Math.max(0, stored - initialStored);
    }

    const noProgress = stored === lastStored && missing === lastMissing;
    if (noProgress && Date.now() - lastKick >= MAIL_STORAGE_WORKER_STALE_MS) {
      staleKicks += 1;
      if (staleKicks >= MAIL_STORAGE_WORKER_MAX_STALE_KICKS) {
        const plan = await fetchInboxArchiveUids(accountId);
        if (plan.ok && plan.data.missing.length > 0) {
          return syncInboxViaClientBatches(
            accountId,
            folder,
            plan.data.missing,
            inboxTarget,
            archiveUids,
            cancelled
          );
        }
        break;
      }
      await kick();
    } else if (!noProgress) {
      staleKicks = 0;
    }
    lastStored = stored;
    lastMissing = missing;

    await delay(MAIL_STORAGE_WORKER_POLL_MS);
  }

  setFolderStorageProgress(accountId, folder, {
    active: false,
    progress: 100,
    label: "Copie interrompue",
  });
  return Math.max(0, lastStored - initialStored);
}

/**
 * Archive INBOX sur DD02 serveur (200 .eml max, FIFO côté PHP).
 */
export async function syncInboxServerStorage(
  accountId: number,
  folder: string,
  uids: number[]
): Promise<number> {
  const key = folderKey(accountId, folder);
  const existing = syncInflight.get(key);
  if (existing) return existing;

  const run = async (): Promise<number> => {
    markInboxArchiveInflight(accountId, true);
    try {
    const { target, missing, archiveUids } = await resolveArchiveUids(accountId, folder, uids);
    if (missing.length === 0) {
      setFolderStorageProgress(accountId, folder, {
        active: false,
        progress: 100,
        label: `Réception à jour (${target}/${target})`,
        done: target,
        total: target,
      });
      void warmStoredAttachmentCache(accountId, folder, archiveUids);
      return 0;
    }

    const isInbox = navFolderSlot(folder, "") === "inbox";
    const statsProbe = await fetchMailStorageStats(accountId);
    const workerEnabled =
      isInbox && statsProbe.ok && statsProbe.data.workerEnabled === true;

    let cancelled = false;
    const abort = () => {
      cancelled = true;
    };

    if (workerEnabled) {
      setFolderStorageProgress(accountId, folder, {
        active: true,
        progress: 0,
        label: `Copie des e-mails… (file serveur)`,
        error: null,
        done: statsProbe.data.inboxStored ?? 0,
        total: target,
        abort,
      });
      return syncInboxViaWorker(accountId, folder, target, archiveUids, () => cancelled);
    }

    setFolderStorageProgress(accountId, folder, {
      active: true,
      progress: 0,
      label: `Copie des e-mails… 0/${missing.length} (objectif ${target})`,
      error: null,
      done: 0,
      total: missing.length,
      abort,
    });

    return syncInboxViaClientBatches(
      accountId,
      folder,
      missing,
      target,
      archiveUids,
      () => cancelled
    );
    } finally {
      markInboxArchiveInflight(accountId, false);
    }
  };

  const promise = run().finally(() => {
    syncInflight.delete(key);
  });
  syncInflight.set(key, promise);
  return promise;
}

/** Archive immédiate d’un nouveau mail INBOX (1 UID). */
export async function fetchNewMailToServerStorage(
  accountId: number,
  folder: string,
  uid: number
): Promise<boolean> {
  if (uid <= 0) return false;
  const storedRes = await fetchMailStoredUids(accountId, folder);
  const storedSet = new Set(
    storedRes.ok && storedRes.data.success ? storedRes.data.uids : []
  );
  if (storedSet.has(uid)) return true;

  const res = await fetchMailEmlBatch({ accountId, folder, uids: [uid] });
  if (!res.ok) return false;
  const stored = await storeFetchedEmlItems(accountId, folder, res.data.items);
  if (stored > 0) notifyMailStorageStatsRefresh();
  return stored > 0;
}

export const syncInboxLocalStorage = syncInboxServerStorage;
export const fetchNewMailToLocalStorage = fetchNewMailToServerStorage;

/** Relance manuelle archivage INBOX (après purge ou échec). */
export function retryInboxServerArchive(accountId: number, folder = "INBOX"): Promise<number> {
  resetInboxArchiveSession(accountId);
  return syncInboxServerStorage(accountId, folder, []);
}
